import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import {
  groupDoctorsByTrainingGroup,
  filterDoctorsByGroup,
  countDoctorsByGroup,
} from "../../../utils/doctorCatalog";
import { DOCTORS_BASE, doctorPhotoUrl, filterAssignedBranches } from "./doctorConstants";
import s from "../clinic.module.css";
import u from "../../ui/ui.module.css";
import {
  Button,
  Card,
  Input,
  Select,
  Avatar,
  EmptyState,
  useToast,
  IconPlus,
  IconSearch,
  IconStethoscope,
} from "../../ui";

function DoctorRegistryRow({ d, branchLabel, onClick }) {
  return (
    <button
      type="button"
      className={`${s.registryRow} ${!d.is_active ? s.registryRowBlocked : ""}`}
      onClick={onClick}
    >
      <div className={s.registryPatient}>
        <Avatar
          name={d.full_name}
          src={doctorPhotoUrl(d.photo_url)}
          size="sm"
        />
        <span className={s.registryName}>
          {d.full_name}
          {d.rating_avg != null && (
            <span className={s.registryRating}>
              {" "}
              ★ {Number(d.rating_avg).toFixed(1)}
            </span>
          )}
        </span>
      </div>
      <span className={s.registryMeta}>
        {(d.groups || []).map((g) => g.name).filter(Boolean).join(", ") || "—"}
      </span>
      <span className={s.registryMeta}>{branchLabel(d)}</span>
      <span className={s.registryMeta}>
        {d.summary?.today_count ?? 0} записей
      </span>
      <span>
        {d.is_active ? (
          <span className={`${u.badge} ${u.badgeGreen}`}>Активен</span>
        ) : (
          <span className={`${u.badge} ${u.badgeRed}`}>Неактивен</span>
        )}
      </span>
    </button>
  );
}

export default function AdminDoctorsList() {
  const navigate = useNavigate();
  const toast = useToast();
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [groups, setGroups] = useState([]);
  const [doctors, setDoctors] = useState([]);

  useEffect(() => {
    clinicAPI
      .getSpecializations()
      .then((r) => setGroups(r.specializations || []))
      .catch(() => setGroups([]));
  }, []);

  const loadList = useCallback(() => {
    const p = { include_summary: "true" };
    if (q.trim()) p.q = q.trim();
    if (activeFilter === "true") p.active = "true";
    if (activeFilter === "false") p.active = "false";
    clinicAPI
      .getDoctors(p)
      .then((r) => setDoctors(r.doctors || []))
      .catch((e) =>
        toast.error(e.response?.data?.error || "Ошибка загрузки"),
      );
  }, [q, activeFilter, toast]);

  useEffect(() => {
    loadList();
  }, [activeFilter, loadList]);

  const activeGroups = useMemo(
    () => groups.filter((g) => g.is_active !== false),
    [groups],
  );

  const groupCounts = useMemo(
    () => countDoctorsByGroup(doctors, groups),
    [doctors, groups],
  );

  const filteredDoctors = useMemo(
    () => filterDoctorsByGroup(doctors, groupFilter || "all"),
    [doctors, groupFilter],
  );

  const doctorSections = useMemo(() => {
    if (groupFilter) {
      const title =
        groupFilter === "none"
          ? "Без группы"
          : activeGroups.find((x) => x.id === groupFilter)?.name || "";
      return [{ key: groupFilter, title, items: filteredDoctors }];
    }
    return groupDoctorsByTrainingGroup(filteredDoctors, groups);
  }, [filteredDoctors, groups, groupFilter, activeGroups]);

  const branchLabel = (d) => {
    const names = filterAssignedBranches(d.branches || [])
      .map((b) => b.name)
      .filter(Boolean);
    return names.length ? names.join(", ") : "—";
  };

  return (
    <Card
      title="Тренеры"
      subtitle={`${filteredDoctors.length} из ${doctors.length}`}
      actions={
        <Button size="sm" onClick={() => navigate("/manage/app/team/invite")}>
          <IconPlus width={16} height={16} />
          Пригласить
        </Button>
      }
    >
      <div className={u.toolbar}>
        <div className={u.searchWrap}>
          <IconSearch className={u.searchIcon} width={18} height={18} />
          <Input
            className={u.searchInput}
            placeholder="ФИО, группа…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadList()}
          />
        </div>
        <Select
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value)}
          style={{ width: 140 }}
        >
          <option value="">Все статусы</option>
          <option value="true">Активные</option>
          <option value="false">Неактивные</option>
        </Select>
        <Button variant="secondary" size="sm" onClick={loadList}>
          Найти
        </Button>
      </div>

      {doctors.length > 0 && (
        <div className={s.doctorSpecFilters}>
          <button
            type="button"
            className={`${s.catalogChip} ${!groupFilter ? s.catalogChipActive : ""}`}
            onClick={() => setGroupFilter("")}
          >
            Все
            <span className={s.catalogChipCount}>{groupCounts.all}</span>
          </button>
          {activeGroups.map((g) => (
            <button
              key={g.id}
              type="button"
              className={`${s.catalogChip} ${groupFilter === g.id ? s.catalogChipActive : ""}`}
              onClick={() => setGroupFilter(g.id)}
            >
              {g.name}
              <span className={s.catalogChipCount}>
                {groupCounts[g.id] ?? 0}
              </span>
            </button>
          ))}
          <button
            type="button"
            className={`${s.catalogChip} ${groupFilter === "none" ? s.catalogChipActive : ""}`}
            onClick={() => setGroupFilter("none")}
          >
            Без группы
            <span className={s.catalogChipCount}>{groupCounts.none ?? 0}</span>
          </button>
        </div>
      )}

      {filteredDoctors.length === 0 ? (
        <EmptyState
          icon={IconStethoscope}
          title={doctors.length ? "Никого не нашли" : "Нет тренеров"}
          description={
            doctors.length
              ? "Смените фильтр или параметры поиска"
              : "Пригласите первого тренера в клуб"
          }
          actionLabel={doctors.length ? undefined : "Пригласить тренера"}
          onAction={
            doctors.length ? undefined : () => navigate("/manage/app/team/invite")
          }
        />
      ) : (
        <div
          className={`${s.registryTable} ${s.registryTableDoctors} ${s.adminDoctorRegistry}`}
        >
          <div className={s.registryHead}>
            <span>Тренер</span>
            <span>Группы</span>
            <span>Залы</span>
            <span>Сегодня</span>
            <span>Статус</span>
          </div>
          {doctorSections.map((section, sectionIndex) => (
            <section
              key={section.key}
              className={s.adminDoctorGroupSection}
              aria-labelledby={
                !groupFilter && section.title
                  ? `doctor-group-${section.key}`
                  : undefined
              }
            >
              {!groupFilter && section.title ? (
                <div
                  className={`${s.adminDoctorGroupDivider} ${sectionIndex === 0 ? s.adminDoctorGroupDividerFirst : ""}`}
                >
                  <h3
                    id={`doctor-group-${section.key}`}
                    className={s.adminDoctorGroupHeading}
                  >
                    <span className={s.adminDoctorGroupAccent} aria-hidden />
                    <span className={s.adminDoctorGroupLabel}>{section.title}</span>
                    <span className={s.adminDoctorGroupCount}>
                      {section.items.length}
                    </span>
                  </h3>
                </div>
              ) : null}
              <div className={s.adminDoctorGroupRows}>
                {section.items.map((d) => (
                  <DoctorRegistryRow
                    key={`${section.key}-${d.id}`}
                    d={d}
                    branchLabel={branchLabel}
                    onClick={() => navigate(`${DOCTORS_BASE}/${d.id}`)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </Card>
  );
}
